import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const initialState = {
  testFunctionsList: [],
  isLoading: false,
};

export const createNewTestFunction = createAsyncThunk(
  "testFunctions/createNew",
  async ({ test_function, test_id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/tests/${test_id}/test_functions`;
    try {
      const response = await axios.post(
        url,
        { test_function: test_function },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);


// Add this thunk inside test_function.js (next to other thunks)
export const getCalculationFieldOptions = createAsyncThunk(
  "testFunctions/getCalculationFieldOptions",
  async (testId, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/tests/${testId}/test_functions?for_calculation=true`;
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data; // [{ id: 123, display_name: "Age" }, ...]
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const copyTestFunction = createAsyncThunk(
  "test_functions/copyTestFunction",
  async ({ testFunction, testId, testFunctionId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/tests/${testId}/test_functions/${testFunctionId}/copy_test_function`;

    try {
      await axios.post(
        url,
        { test_function: testFunction },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return { testFunctionId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllTestFunctions = createAsyncThunk(
  "testFunctions/getAll",
  async ({ test_id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/tests/${test_id}/test_functions`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const editTestFunction = createAsyncThunk(
  "test_functions/editTestFunction",
  async ({ testFunction, testId, testFunctionId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/tests/${testId}/test_functions/${testFunctionId}`;

    try {
      await axios.put(
        url,
        { test_function: testFunction },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return { testFunctionId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTestFunction = createAsyncThunk(
  "test_functions/deleteTestFunction",
  async ({ testId, testFunctionId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/tests/${testId}/test_functions/${testFunctionId}`;

    try {
      await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });

      toast.success("Test function deleted successfully");
      return { testFunctionId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const testFunctionSlice = createSlice({
  name: "testFunction",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllTestFunctions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllTestFunctions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.testFunctionsList = action.payload;
      })
      .addCase(getAllTestFunctions.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteTestFunction.fulfilled, (state, action) => {
        state.testFunctionsList = state.testFunctionsList.filter(
          (testFunction) => testFunction.id !== action.payload.testFunctionId
        );
      })
      .addCase(createNewTestFunction.fulfilled, (state, action) => {
        state.testFunctionsList.push(action.payload); // Set the list to an empty array
      });
  },
});

export const addCalculatedTestFunction = createAsyncThunk(
  'testFunction/addCalculatedTestFunction',
  async (newFunction) => {
    const bearerToken = getToken();
    const response = await axios.post(`${apiBaseUrl}/calculated_test_functions`, newFunction, {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    return response.data; // Return the response data
  }
);

export const deleteCalculatedTestFunction = createAsyncThunk(
  'testFunction/deleteCalculatedTestFunction',
  async (functionId, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/calculated_test_functions/${functionId}`;

    try {
      await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Calculated test function deleted successfully.");
    } catch (error) {
      toast.error("Failed to delete calculated test function.");
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateCalculatedTestFunction = createAsyncThunk(
  'testFunction/updateCalculatedTestFunction',
  async ({ name, implementation, id }) => {
    const bearerToken = getToken();
    const response = await axios.put(`${apiBaseUrl}/calculated_test_functions/${id}`, {
      name,
      implementation,
    }, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    return response.data; // Return the updated function data
  }
);

export default testFunctionSlice.reducer;
